import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Link,
  Outlet,
} from "@tanstack/react-router";
import { buttonClasses } from "@fiskhub/ui";
import { AppShell } from "@/components/app-shell";
import { LoginScreen } from "@/routes/login";

/**
 * Screens are code-split per route. The dashboard pulls in a charting library
 * and the invoice detail pulls in the XML viewer; neither belongs in the bundle
 * an operator downloads to look at the invoice list. `defaultPreload: "intent"`
 * fetches the chunk on hover, so the split is invisible in practice.
 */
const DashboardScreen = lazyRouteComponent(() => import("@/routes/dashboard"), "DashboardScreen");
const CompaniesScreen = lazyRouteComponent(() => import("@/routes/companies"), "CompaniesScreen");
const CompanyDetailScreen = lazyRouteComponent(
  () => import("@/routes/company-detail"),
  "CompanyDetailScreen",
);
const CertificatesScreen = lazyRouteComponent(
  () => import("@/routes/certificates"),
  "CertificatesScreen",
);
const DevicesScreen = lazyRouteComponent(() => import("@/routes/devices"), "DevicesScreen");
const InvoicesScreen = lazyRouteComponent(() => import("@/routes/invoices"), "InvoicesScreen");
const InvoiceDetailScreen = lazyRouteComponent(
  () => import("@/routes/invoice-detail"),
  "InvoiceDetailScreen",
);
const UsersScreen = lazyRouteComponent(() => import("@/routes/users"), "UsersScreen");
const InviteScreen = lazyRouteComponent(() => import("@/routes/invite"), "InviteScreen");
const ApiKeysScreen = lazyRouteComponent(() => import("@/routes/api-keys"), "ApiKeysScreen");
const PlaygroundScreen = lazyRouteComponent(
  () => import("@/routes/playground"),
  "PlaygroundScreen",
);
const AuditScreen = lazyRouteComponent(() => import("@/routes/audit"), "AuditScreen");

function optional(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function NotFound() {
  return (
    <div className="mx-auto max-w-lg space-y-4 p-12 text-center">
      <h1 className="text-xl font-semibold">Page not found</h1>
      <p className="text-sm text-muted-foreground">
        That address does not match any screen in the dashboard.
      </p>
      <Link to="/" className={buttonClasses({ variant: "outline", size: "sm" })}>
        Back to the dashboard
      </Link>
    </div>
  );
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFound,
});

export interface LoginSearch {
  /**
   * Which organisation to sign in to — `/login?slug=acme`.
   *
   * `users` is `UNIQUE (tenant_id, email)`, not globally unique, so the API
   * needs tenant context before it can look an email up at all. It resolves
   * that from the subdomain, or from a sole active tenant, and otherwise
   * refuses. This is the third way in: a shared hostname with the tenant named
   * in the link, which is what a support or invite URL can carry.
   */
  slug?: string;
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginScreen,
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    slug: optional(search["slug"]),
  }),
});

export interface InviteSearch {
  /**
   * The one-time token from the invitation email — `/invite?token=…`.
   *
   * Outside the shell, like `/login`: the person opening it has no session
   * yet, and the point of the screen is to give them one. The token is sent to
   * the API in a request body, never echoed anywhere else.
   */
  token?: string;
}

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite",
  component: InviteScreen,
  validateSearch: (search: Record<string, unknown>): InviteSearch => ({
    token: optional(search["token"]),
  }),
});

/** Pathless layout route: everything inside it requires a session. */
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "shell",
  component: AppShell,
});

const dashboardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/",
  component: DashboardScreen,
});

const companiesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/companies",
  component: CompaniesScreen,
});

const companyDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/companies/$companyId",
  component: CompanyDetailScreen,
});

export interface CertificateSearch {
  company?: string;
}

const certificatesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/certificates",
  component: CertificatesScreen,
  validateSearch: (search: Record<string, unknown>): CertificateSearch => ({
    company: optional(search["company"]),
  }),
});


export interface DeviceSearch {
  company?: string;
  premises_code?: string;
  /** `missing` narrows to devices that cannot fiscalize. */
  isu?: string;
}

const devicesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/devices",
  component: DevicesScreen,
  validateSearch: (search: Record<string, unknown>): DeviceSearch => ({
    company: optional(search["company"]),
    premises_code: optional(search["premises_code"]),
    isu: optional(search["isu"]),
  }),
});

export interface InvoiceSearch {
  company?: string;
  device?: string;
  premises_code?: string;
  status?: string;
  from?: string;
  to?: string;
  q?: string;
  cursor?: string;
}

const invoicesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/invoices",
  component: InvoicesScreen,
  validateSearch: (search: Record<string, unknown>): InvoiceSearch => ({
    company: optional(search["company"]),
    device: optional(search["device"]),
    premises_code: optional(search["premises_code"]),
    status: optional(search["status"]),
    from: optional(search["from"]),
    to: optional(search["to"]),
    q: optional(search["q"]),
    cursor: optional(search["cursor"]),
  }),
});

const invoiceDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/invoices/$invoiceId",
  component: InvoiceDetailScreen,
});

const usersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/users",
  component: UsersScreen,
});

export interface AuditSearch {
  action?: string;
  q?: string;
}

const apiKeysRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/api-keys",
  component: ApiKeysScreen,
});

const playgroundRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/playground",
  component: PlaygroundScreen,
});

const auditRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/audit",
  component: AuditScreen,
  validateSearch: (search: Record<string, unknown>): AuditSearch => ({
    action: optional(search["action"]),
    q: optional(search["q"]),
  }),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  inviteRoute,
  shellRoute.addChildren([
    dashboardRoute,
    companiesRoute,
    companyDetailRoute,
    certificatesRoute,
    devicesRoute,
    invoicesRoute,
    invoiceDetailRoute,
    usersRoute,
    apiKeysRoute,
    playgroundRoute,
    auditRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultNotFoundComponent: NotFound,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
